import * as React from 'react';
import moment from 'moment';
import {transparentize} from 'polished';
import styled from 'styled-components';
import BoxContainer from 'containers/BoxContainer';
import {Container as BaseButton, ButtonVariant} from 'components/Button';
import {Container as SocialButton, SocialType} from 'components/SocialButton';
import UserAvatar from 'components/UserAvatar';
import {ProfileUser} from '../types';
import {getGenderLabel, prepareProfileData} from '../helpers';

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: 240px minmax(375px, 680px);
  gap: 10px;
  justify-content: center;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const AvatarBox = styled.div`
  margin-bottom: 12px;
`;

const InfoItem = styled.div`
  display: grid;
  grid-template-columns: 150px 1fr;
  gap: 10px;
  font-size: 14px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
    gap: 5px;
  }
`;

const Contacts = styled.div`
  display: grid;
  gap: 6px;
  margin-top: 20px;
  
  ${InfoItem} {
    grid-template-columns: repeat(2, max-content);
    gap: 4px;
  }
`;

const Socials = styled.span`
  margin-top: 14px;
  margin-right: -12px;
  margin-bottom: -12px;
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  
  ${SocialButton} {
    margin-right: 12px;
    margin-bottom: 12px;
    width: 24px;
    height: 24px;
  }
`;

const Main = styled(BoxContainer)`
  align-self: start;
  padding: 20px;
  border-width: 1px;
  
  @media (max-width: 1024px) {
    display: grid;
    grid-template-columns: repeat(3, max-content);
    gap: 25px;
    align-items: start;
  }

  @media (max-width: 480px) {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`;

const About = styled(BoxContainer)`
  padding: 20px;
  border-width: 1px;
`;

const Header = styled.div`
  padding-bottom: 20px;
  border-bottom: ${({theme}) => `
      1px solid ${transparentize(.95, theme.colors.white)}
  `};
`;

const Buttons = styled.div`
  display: grid;
  gap: 6px;
`;

const Button = styled(BaseButton)`
  width: 200px;
  height: 44px;
  border-radius: 5px;
`;

const Nickname = styled.div`
  margin-bottom: 10px;
  font-weight: 500;
  color: ${({theme}) => theme.colors.blueViolet};
`;

const Name = styled.div`
  font-size: 22px;
  font-weight: 600;
  line-height: 29px;
`;

const Info = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  margin: 20px 0;
`;

const ItemLabel = styled.div`
  color: ${({theme}) => `
    ${transparentize(.6, theme.colors.white)}
  `};
`;

const ItemValue = styled.div`
  font-size: 14px;
  line-height: 18px;
  white-space: pre-wrap;
`;

/**
 * The component render summary section
 * @param {ProfileUser} props
 * @return {React.ReactElement}
 * @constructor
 */
export default function ProfileView(
    props: ProfileUser,
): React.ReactElement {
  const data = prepareProfileData(props);
  const {
    name,
    gender,
    surname,
    nickname,
    avatar,
    info,
    contacts,
  } = data;

  const tempEmptyFunction = () => {
    // todo delete this
  };

  /**
   * The function render profile info items
   * @return {React.ReactNodeArray}
   */
  function renderInfoItems() {
    const result: React.ReactNodeArray = [];

    const {
      about,
      birthday,
      city,
      education,
      job,
    } = info;

    result.push(renderInfoItem('О себе', about));
    result.push(
        renderInfoItem(
            'Дата рождения ',
            birthday && moment(birthday).format('DD MMMM YYYY'),
        ),
    );
    result.push(renderInfoItem('Пол', getGenderLabel(gender)));
    result.push(renderInfoItem('Город', city));
    result.push(renderInfoItem('Образование', education));
    result.push(renderInfoItem('Место работы', job));

    return result.filter(Boolean);
  }

  /**
   * The function render profile info item
   * @param {string} label
   * @param {string} value
   * @return {React.ReactElement | void}
   */
  function renderInfoItem(label: string, value: string | undefined) {
    if (label && value) {
      return (
        <InfoItem>
          <ItemLabel>{label}</ItemLabel>
          <ItemValue>{value}</ItemValue>
        </InfoItem>
      );
    }
  }

  /**
   * The function render profile socials
   * @return {React.ReactNodeArray}
   */
  function renderSocials():React.ReactNodeArray {
    const socials = [];

    if (data.socials.telegram) {
      socials.push(<SocialButton type={SocialType.Telegram}/>);
    }
    if (data.socials.instagram) {
      socials.push(<SocialButton type={SocialType.Instagram}/>);
    }
    if (data.socials.vkontakte) {
      socials.push(<SocialButton type={SocialType.Vkontakte}/>);
    }

    return socials;
  }

  /**
   * The function render contact button
   * @return {React.ReactElement}
   */
  function renderContactButton() {
    return props.preferredCommunication && (
      <Button
        onClick={tempEmptyFunction}
        variant={ButtonVariant.Primary}
        stretched
      >
        Связаться
      </Button>
    );
  }

  /**
   * The function render video button
   * @return {React.ReactElement}
   */
  function renderVideoButton() {
    return props.video && (
      <Button
        onClick={tempEmptyFunction}
        variant={ButtonVariant.Outline}
        stretched
      >
        Видео-визитка
      </Button>
    );
  }

  return (
    <Wrapper>
      <Main>
        <div>
          <AvatarBox>
            <UserAvatar preview={avatar}/>
          </AvatarBox>
        </div>
        <div>
          <Buttons>
            {renderContactButton()}
            {renderVideoButton()}
          </Buttons>
          <Contacts>
            {renderInfoItem('E-mail:', contacts?.email)}
            {renderInfoItem('Телефон', contacts?.phone)}
          </Contacts>
          <Socials>
            {renderSocials()}
          </Socials>
        </div>

      </Main>
      <About>
        <Header>
          <Nickname>
            {nickname}
          </Nickname>
          <Name>
            {`${name} ${surname}`}
          </Name>
        </Header>
        <Info>
          {renderInfoItems()}
        </Info>
      </About>
    </Wrapper>
  );
};
